import { App, Button, Result } from 'antd';
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createFixedIdentitySession } from '../api/active/developmentIdentity';
import {
  requestPendingSaveLeaveCheck,
  requestRoleViewSwitch,
} from '../api/active/roleViewEditorGuard';
import {
  ActiveSessionProvider,
  ActiveSessionShell,
  isOfflineDemoSessionEnabled,
  useOptionalActiveSession,
} from '../api/active/sessionProvider';
import { createActiveTransport } from '../api/active/transport';
import { ActiveOperationLogPageRuntimeProvider } from '../audit-integration/operationLogPageRuntime';
import { ActiveCustomerPageRuntimeProvider } from '../customer-integration/customerPageRuntime';
import { ActiveEquipmentPageRuntimeProvider } from '../equipment-integration/equipmentPageRuntime';
import { ActiveInventoryReadRuntimeProvider } from '../inventory-integration/inventoryPageRuntime';
import { MarketingAnnouncementPrefetchProvider } from '../marketing-announcement/MarketingAnnouncementPrefetch';
import { ActivePackagePageRuntimeProvider } from '../package-integration/packagePageRuntime';
import { AppShell } from '../pages/cpq-v2/components/AppShell';
import { MainRouter } from '../pages/cpq-v2/components/MainRouter';
import {
  CASE_SHOWCASE_SHARE_TOKEN_PREFIX,
  readCaseShowcaseShareToken,
} from '../pages/cpq-v2/mock/caseShowcaseShare';
import {
  getLocalDemoCatalog,
  loadLocalDemoState,
  localDemoStorage,
  refreshLocalDemoCatalog,
} from '../pages/cpq-v2/mock/localDemoCatalog';
import type { AcceptanceIdentityPort } from '../pages/cpq-v2/state/acceptanceIdentity';
import { CpqProvider, useCpq } from '../pages/cpq-v2/state/CpqContext';
import { isCasePreviewEnabled } from '../pages/cpq-v2/state/caseFeature';
import {
  createDeviceAssetStore,
  DeviceAssetStorageError,
  type DeviceAssetStore,
  DeviceAssetStoreProvider,
  hydrateDeviceMediaState,
} from '../pages/cpq-v2/state/deviceAssetStore';
import {
  CPQ_LEGACY_STORAGE_KEY,
  CPQ_STORAGE_KEY,
  CPQ_V4_STORAGE_KEY,
  type CpqLoadResult,
  type CpqLoadStatus,
  createDisposableStorageRepository,
  loadCpqState,
} from '../pages/cpq-v2/state/repository';
import {
  createFailClosedBusinessStorage,
  type StorageFailureDiagnostic,
  storageFailureDiagnostic,
  storageFailureSummary,
} from '../pages/cpq-v2/state/storageFailure';
import type { CpqState, StorageLike } from '../pages/cpq-v2/state/types';
import { createDisposableFieldValuePort } from '../pages/cpq-v2/state/unifiedFieldPort';
import {
  createWorkspacePreferencesPort,
  WORKSPACE_UI_STORAGE_KEY,
} from '../pages/cpq-v2/state/workspacePreferences';
import styles from '../pages/cpq-v2/styles.module.css';
import { ActivePermissionPageRuntimeProvider } from '../permission-integration/permissionPageRuntime';
import { ActivePlanPageRuntimeProvider } from '../plan-integration/planPageRuntime';
import { readPublicShareToken } from '../quote-integration/customerShareUrl';
import {
  createFormalWorkspaceRecoveryStore,
  type FormalWorkspaceRecoveryPort,
} from '../quote-integration/formalWorkspaceRecoveryStore';
import {
  createFormalWorkspaceSessionScope,
  formalWorkspaceSessionStorageKey,
} from '../quote-integration/formalWorkspaceSession';
import { createPublicShareApi } from '../quote-integration/publicShareApi';
import { ActiveQuotePageRuntimeProvider } from '../quote-integration/quotePageRuntime';
import shellStyles from './adapters/disposableLocalStorageDemo.module.css';

export const DISPOSABLE_ACCEPTANCE_IDENTITY_KEY =
  'cpq-v2-acceptance-identity:v1';

export const DISPOSABLE_CPQ_STORAGE_KEYS = [
  CPQ_STORAGE_KEY,
  CPQ_V4_STORAGE_KEY,
  CPQ_LEGACY_STORAGE_KEY,
] as const;

const DISPOSABLE_DEMO_LOADABLE_STATUSES = [
  'missing',
  'valid-v5',
  'migrated-v4',
  'migrated-v3',
] as const satisfies readonly CpqLoadStatus[];

function hasTestRuntime() {
  return (
    typeof process !== 'undefined' &&
    (process.env.NODE_ENV === 'test' || process.env.VITEST === 'true')
  );
}

function LocalDemoCatalogBridge() {
  const { commit, mutateTransient } = useCpq();
  const commitRef = useRef(commit);
  const transientRef = useRef(mutateTransient);
  commitRef.current = commit;
  transientRef.current = mutateTransient;
  useEffect(() => {
    if (!getLocalDemoCatalog()) return;
    const refresh = () => commitRef.current(refreshLocalDemoCatalog);
    const storageChanged = (event: StorageEvent) => {
      if (
        event.key === 'cpq-local-database-demo:v1:catalog' ||
        event.key === 'cpq-local-database-demo:v1:module-state'
      )
        refresh();
      if (event.key === `cpq-local-database-demo:v1:${CPQ_STORAGE_KEY}`) {
        const loaded = loadCpqState(localDemoStorage(window.localStorage));
        if (loaded.status !== 'valid-v5') return;
        transientRef.current((current) => ({
          ...loaded.state,
          currentRoleId: current.currentRoleId,
          role: current.role,
          currentActorUserId: current.currentActorUserId,
          activeView: current.activeView,
          activeQuoteId: current.activeQuoteId,
          activeQuoteVersionId: current.activeQuoteVersionId,
          openQuoteIds: current.openQuoteIds,
          workspacePicker: current.workspacePicker,
          quoteManagementDetail: current.quoteManagementDetail,
          quotes: {
            ...loaded.state.quotes,
            ...Object.fromEntries(
              Object.entries(current.quotes).filter(
                ([id, quote]) => quote.dirty || !loaded.state.quotes[id],
              ),
            ),
          },
        }));
      }
    };
    window.addEventListener('cpq-local-demo-catalog-changed', refresh);
    window.addEventListener('cpq-local-demo-modules-changed', refresh);
    window.addEventListener('storage', storageChanged);
    return () => {
      window.removeEventListener('cpq-local-demo-catalog-changed', refresh);
      window.removeEventListener('cpq-local-demo-modules-changed', refresh);
      window.removeEventListener('storage', storageChanged);
    };
  }, []);
  return null;
}

function RecoverableSessionShell(
  props: ComponentProps<typeof ActiveSessionShell>,
) {
  const {
    workspaceSessionScope,
    getCurrentState,
    ensureFormalWorkspaceRecovery,
    isFormalWorkspaceRecoveryCurrent,
  } = useCpq();
  const preserveDraft = useCallback(async () => {
    if (!workspaceSessionScope || getCurrentState().activeView !== 'workspace')
      return false;
    requestPendingSaveLeaveCheck();
    if (!requestRoleViewSwitch().allowed) return false;
    const checkpoint = await ensureFormalWorkspaceRecovery(true);
    requestPendingSaveLeaveCheck();
    return (
      getCurrentState().activeView === 'workspace' &&
      requestRoleViewSwitch().allowed &&
      checkpoint !== null &&
      isFormalWorkspaceRecoveryCurrent(checkpoint)
    );
  }, [
    workspaceSessionScope,
    getCurrentState,
    ensureFormalWorkspaceRecovery,
    isFormalWorkspaceRecoveryCurrent,
  ]);
  return (
    <ActiveSessionShell {...props} beforeReauthentication={preserveDraft} />
  );
}

export function isDisposableDemoLoadableStatus(
  status: CpqLoadStatus,
): status is (typeof DISPOSABLE_DEMO_LOADABLE_STATUSES)[number] {
  return (DISPOSABLE_DEMO_LOADABLE_STATUSES as readonly string[]).includes(
    status,
  );
}

/**
 * The sole bridge from persisted business references to this origin's Blob
 * URLs. Initial mount and cross-tab refreshes must both call this function;
 * otherwise a raw cpq-device-asset:// reference can transiently reach img.
 */
async function hydrateDisposableDeviceMediaLoad(
  loaded: CpqLoadResult,
  deviceAssetStore: DeviceAssetStore,
): Promise<CpqLoadResult> {
  return {
    ...loaded,
    state: await hydrateDeviceMediaState(loaded.state, deviceAssetStore),
  };
}

/**
 * Clears only this disposable CPQ demo's known business and tab-identity
 * keys. It deliberately does not call Storage.clear(), so unrelated apps in
 * the same browser origin remain untouched. Any storage failure is allowed to
 * propagate so the caller can remain fail-closed.
 */
export function clearDisposableDemoStorage(
  businessStorage: StorageLike,
  acceptanceStorage?: StorageLike,
): void {
  for (const key of DISPOSABLE_CPQ_STORAGE_KEYS)
    businessStorage.removeItem(key);
  acceptanceStorage?.removeItem(DISPOSABLE_ACCEPTANCE_IDENTITY_KEY);
}

export function UnmigratedModulesProvider({
  storage,
  runtime,
  sessionShell,
}: {
  storage: StorageLike;
  runtime?: {
    workspaceRecoveryPort?: FormalWorkspaceRecoveryPort;
    acceptanceIdentityPort?: AcceptanceIdentityPort;
    maximumIntegrationPermissions?: boolean;
    deviceAssetStore?: DeviceAssetStore;
    refreshStorage?: StorageLike;
    subscribeBusinessStorage?: (listener: () => void) => () => void;
  };
  sessionShell?: {
    developmentIdentityEnabled: boolean;
    developmentIdentityKey: string;
    wecomLoginEnabled: boolean;
  };
}) {
  function UnmigratedModulesRuntime() {
    const { message } = App.useApp();
    const activeSession = useOptionalActiveSession();
    const workspaceSessionScope = useMemo(
      () =>
        activeSession?.state.status === 'ready' &&
        !activeSession.offlineDemoEnabled
          ? createFormalWorkspaceSessionScope(activeSession.state.user)
          : undefined,
      [activeSession?.state],
    );
    const workspaceRecoveryPort = useMemo(
      () =>
        runtime?.workspaceRecoveryPort ??
        createFormalWorkspaceRecoveryStore({ legacyStorage: storage }),
      [runtime?.workspaceRecoveryPort, storage],
    );
    const scopeCompany = workspaceSessionScope?.companyId;
    const scopeActor = workspaceSessionScope?.actorUserId;
    const scopeAuthorization = workspaceSessionScope?.authorizationKey;
    const workspaceScopeKey = workspaceSessionScope
      ? formalWorkspaceSessionStorageKey(workspaceSessionScope)
      : null;
    const [workspaceRecoveryRead, setWorkspaceRecoveryRead] = useState<{
      key: string;
      snapshot: string | null;
      status: 'ready' | 'error';
      error?: unknown;
    } | null>(null);
    const [workspaceRecoveryReadRevision, setWorkspaceRecoveryReadRevision] =
      useState(0);
    useEffect(() => {
      if (
        scopeCompany === undefined ||
        scopeActor === undefined ||
        scopeAuthorization === undefined
      )
        return;
      const scope = {
        companyId: scopeCompany,
        actorUserId: scopeActor,
        authorizationKey: scopeAuthorization,
      };
      const key = formalWorkspaceSessionStorageKey(scope);
      let active = true;
      setWorkspaceRecoveryRead(null);
      void workspaceRecoveryPort
        .load(scope)
        .then((snapshot) => {
          if (active)
            setWorkspaceRecoveryRead({ key, snapshot, status: 'ready' });
        })
        .catch((error: unknown) => {
          if (active)
            setWorkspaceRecoveryRead({
              key,
              snapshot: null,
              status: 'error',
              error,
            });
        });
      return () => {
        active = false;
      };
    }, [
      scopeCompany,
      scopeActor,
      scopeAuthorization,
      workspaceRecoveryPort,
      workspaceRecoveryReadRevision,
    ]);
    const [persistenceFailure, setPersistenceFailure] =
      useState<StorageFailureDiagnostic | null>(null);
    const blockPersistence = useCallback(
      (failure: StorageFailureDiagnostic) => {
        message.destroy();
        setPersistenceFailure((first) => first ?? failure);
      },
      [message],
    );
    const guardedStorage = useMemo<StorageLike>(
      () => createFailClosedBusinessStorage(storage, blockPersistence),
      [blockPersistence, storage],
    );
    const deviceAssetStore = useMemo(
      () => runtime?.deviceAssetStore ?? createDeviceAssetStore(),
      [runtime?.deviceAssetStore],
    );
    const baseRepository = useMemo(
      () => createDisposableStorageRepository(guardedStorage),
      [guardedStorage],
    );
    const [rawLoad] = useState(() => loadLocalDemoState(baseRepository.load()));
    const [hydratedLoad, setHydratedLoad] = useState<CpqLoadResult | null>(
      () => (hasTestRuntime() ? rawLoad : null),
    );
    const [assetHydrationError, setAssetHydrationError] =
      useState<unknown>(null);
    useEffect(() => {
      let active = true;
      void hydrateDisposableDeviceMediaLoad(rawLoad, deviceAssetStore)
        .then((load) => {
          if (!active) return;
          setHydratedLoad(load);
        })
        .catch((error: unknown) => {
          if (!active) return;
          setAssetHydrationError(error);
        });
      return () => {
        active = false;
      };
    }, [deviceAssetStore, rawLoad]);
    const repositoryPort = useMemo(() => {
      if (!hydratedLoad) return null;
      return {
        ...baseRepository,
        load: () => hydratedLoad,
        refresh: () =>
          hydrateDisposableDeviceMediaLoad(
            loadLocalDemoState(
              loadCpqState(runtime?.refreshStorage ?? guardedStorage),
            ),
            deviceAssetStore,
          ),
        subscribe: runtime?.subscribeBusinessStorage,
        reset() {
          void deviceAssetStore.clear();
          return baseRepository.reset();
        },
      };
    }, [baseRepository, deviceAssetStore, guardedStorage, hydratedLoad]);
    const preferencesPort = useMemo(() => {
      const base = createWorkspacePreferencesPort(storage);
      return {
        load: base.load,
        save(preferences: Parameters<typeof base.save>[0]) {
          try {
            base.save(preferences);
            message.destroy('workspace-preferences-storage');
          } catch (cause) {
            const failure = storageFailureDiagnostic(
              cause,
              'setItem',
              WORKSPACE_UI_STORAGE_KEY,
              JSON.stringify(preferences),
            );
            message.warning({
              key: 'workspace-preferences-storage',
              content: `工作台显示偏好未保存，当前设置仅在本页生效。${import.meta.env.DEV ? storageFailureSummary(failure) : ''}`,
            });
          }
        },
      };
    }, [message, storage]);
    const fieldValuePort = useMemo(() => createDisposableFieldValuePort(), []);
    const acceptanceIdentityPort = useMemo(
      () =>
        runtime?.maximumIntegrationPermissions && hydratedLoad
          ? createMaximumIntegrationAcceptanceIdentityPort(hydratedLoad.state)
          : runtime?.acceptanceIdentityPort,
      [
        hydratedLoad,
        runtime?.acceptanceIdentityPort,
        runtime?.maximumIntegrationPermissions,
      ],
    );

    if (persistenceFailure) {
      return (
        <Result
          status="error"
          title="CPQ 已阻断数据适配"
          subTitle={`Disposable LocalStorage Mock 持久化失败；业务界面已卸载，后续写操作已停止。${import.meta.env.DEV ? storageFailureSummary(persistenceFailure) : ''}`}
        />
      );
    }

    if (assetHydrationError) {
      return (
        <Result
          status="error"
          title="设备图片资产不可用"
          subTitle={
            assetHydrationError instanceof DeviceAssetStorageError
              ? assetHydrationError.message
              : '历史设备图片未能迁移到浏览器资产存储，未挂载业务页面；请刷新后重试。'
          }
        />
      );
    }

    if (!repositoryPort) {
      return (
        <Result
          status="info"
          title="正在准备图片资源"
          subTitle="准备完成后将自动进入工作区。"
        />
      );
    }

    // Unknown recovery data must not mount a provider that can auto-write an empty snapshot.
    // Signed-out sessions bypass this gate so their normal login UI remains available.
    if (workspaceScopeKey && workspaceRecoveryRead?.key !== workspaceScopeKey)
      return <Result status="info" title="正在恢复本地工作区" />;
    if (workspaceScopeKey && workspaceRecoveryRead?.status === 'error')
      return (
        <Result
          status="warning"
          title="本地工作区暂时无法恢复"
          subTitle="原有恢复记录已保留，请重试读取。"
          extra={
            <Button
              onClick={() =>
                setWorkspaceRecoveryReadRevision((revision) => revision + 1)
              }
            >
              重试读取工作区
            </Button>
          }
        />
      );

    const application = (
      <ActiveCustomerPageRuntimeProvider>
        <ActivePermissionPageRuntimeProvider>
          <ActiveEquipmentPageRuntimeProvider>
            <ActivePackagePageRuntimeProvider>
              <ActivePlanPageRuntimeProvider>
                <ActiveInventoryReadRuntimeProvider>
                  <ActiveOperationLogPageRuntimeProvider>
                    <ActiveQuotePageRuntimeProvider>
                      <div className={styles.scope}>
                        <LocalDemoCatalogBridge />
                        <AppShell>
                          <MainRouter />
                        </AppShell>
                      </div>
                    </ActiveQuotePageRuntimeProvider>
                  </ActiveOperationLogPageRuntimeProvider>
                </ActiveInventoryReadRuntimeProvider>
              </ActivePlanPageRuntimeProvider>
            </ActivePackagePageRuntimeProvider>
          </ActiveEquipmentPageRuntimeProvider>
        </ActivePermissionPageRuntimeProvider>
      </ActiveCustomerPageRuntimeProvider>
    );

    return (
      <div className={shellStyles.demoRuntime}>
        <div
          className={shellStyles.businessViewport}
          data-device-assets-hydrated={
            hydratedLoad === rawLoad ? 'false' : 'true'
          }
          data-testid="disposable-demo-business-viewport"
        >
          <DeviceAssetStoreProvider store={deviceAssetStore}>
            <CpqProvider
              key={
                workspaceSessionScope
                  ? formalWorkspaceSessionStorageKey(workspaceSessionScope)
                  : 'signed-out'
              }
              repositoryPort={repositoryPort}
              preferencesPort={preferencesPort}
              fieldValuePort={fieldValuePort}
              acceptanceIdentityPort={acceptanceIdentityPort}
              workspaceRecoveryPort={workspaceRecoveryPort}
              initialWorkspaceRecovery={
                workspaceRecoveryRead?.key === workspaceScopeKey
                  ? workspaceRecoveryRead.snapshot
                  : null
              }
              workspaceSessionScope={workspaceSessionScope}
              transientRoutes
            >
              {sessionShell ? (
                <RecoverableSessionShell {...sessionShell}>
                  {application}
                </RecoverableSessionShell>
              ) : (
                application
              )}
            </CpqProvider>
          </DeviceAssetStoreProvider>
        </div>
      </div>
    );
  }

  return <UnmigratedModulesRuntime />;
}

export function QuotationSystemRoot() {
  const token = useSyncExternalStore(
    subscribePublicHash,
    () => readPublicShareToken(window.location.hash),
    () => null,
  );
  const publicPort = useMemo(
    () =>
      createPublicShareApi(
        createActiveTransport(`${window.location.origin}/api/v1`),
      ),
    [],
  );
  const offlineDemo = isOfflineDemoSessionEnabled({
    dev: import.meta.env.DEV,
    flag: import.meta.env.VITE_CPQ_OFFLINE_DEMO,
    hostname: window.location.hostname,
  });
  const caseShowcaseShare = useMemo(
    () =>
      token === null || !isCasePreviewEnabled()
        ? undefined
        : readCaseShowcaseShareToken(token),
    [token],
  );
  if (
    token?.startsWith(CASE_SHOWCASE_SHARE_TOKEN_PREFIX) &&
    !isCasePreviewEnabled()
  )
    return <Result status="404" title="链接不可用" />;
  // CASEMOCK shares use the same public hash shape and full-screen shell as the
  // restored branch, but are decoded entirely from the explicit fixture token.
  if (caseShowcaseShare !== undefined)
    return (
      <div className={`${styles.scope} ${styles.caseShareScope}`}>
        <AppShell publicAccess>
          <MainRouter caseShowcaseShare={caseShowcaseShare} />
        </AppShell>
      </div>
    );
  // The existing share route is anonymous. Do not even read browser business
  // storage or mount the internal session/identity/catalog providers here.
  if (token !== null && !offlineDemo)
    return (
      <div className={styles.scope}>
        <AppShell publicAccess>
          <MainRouter publicAccess={{ token, port: publicPort }} />
        </AppShell>
      </div>
    );
  return <InternalQuotationSession />;
}

function subscribePublicHash(listener: () => void) {
  window.addEventListener('hashchange', listener);
  return () => window.removeEventListener('hashchange', listener);
}

export function isApprovedLocalDevelopmentHost(
  hostname: string,
  lanFixedIdentityEnabled: boolean,
): boolean {
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1'
  )
    return true;
  if (!lanFixedIdentityEnabled) return false;
  const octets = hostname.split('.').map(Number);
  if (
    octets.length !== 4 ||
    octets.some((value) => !Number.isInteger(value) || value < 0 || value > 255)
  )
    return false;
  return (
    octets[0] === 10 ||
    (octets[0] === 192 && octets[1] === 168) ||
    (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31)
  );
}

function InternalQuotationSession() {
  const storage = useMemo<StorageLike>(() => {
    const base = window.localStorage;
    if (!getLocalDemoCatalog()) return base;
    return localDemoStorage(base);
  }, []);
  const developmentIdentityEnabled = import.meta.env.DEV;
  const localDevelopmentHost = isApprovedLocalDevelopmentHost(
    window.location.hostname,
    import.meta.env.VITE_CPQ_LAN_FIXED_IDENTITY_ENABLED === 'true',
  );
  const wecomLoginEnabled =
    import.meta.env.VITE_WECOM_LOGIN_ENABLED === 'true' &&
    !localDevelopmentHost;
  const maximumIntegrationPermissions =
    import.meta.env.VITE_INTEGRATION_MAX_PERMISSIONS_ENABLED === 'true';
  let loaded: ReturnType<typeof loadCpqState>;
  let acceptanceStorage: StorageLike;
  try {
    acceptanceStorage = window.sessionStorage;
    loaded = loadCpqState(storage);
    if (
      !isDisposableDemoLoadableStatus(loaded.status) &&
      loaded.status !== 'storage-read-error'
    ) {
      clearDisposableDemoStorage(storage, acceptanceStorage);
      loaded = loadCpqState(storage);
    }
  } catch {
    return (
      <Result
        status="error"
        title="CPQ 已阻断数据适配"
        subTitle="Disposable Demo 旧数据清理或重建失败，已停止挂载。"
      />
    );
  }
  if (!isDisposableDemoLoadableStatus(loaded.status)) {
    return (
      <Result
        status="error"
        title="CPQ 已阻断数据适配"
        subTitle={`检测到 ${loaded.status}，不会回退默认 Demo。`}
      />
    );
  }
  return (
    <ActiveSessionProvider
      automaticDevelopmentIdentityKey={
        developmentIdentityEnabled && localDevelopmentHost
          ? 'superadmin'
          : undefined
      }
      developmentIdentity={(transport, identityKey) =>
        createFixedIdentitySession(transport, {
          environment: 'local',
          identityKey,
        })
      }
    >
      <MarketingAnnouncementPrefetchProvider>
        <UnmigratedModulesProvider
          storage={storage}
          sessionShell={{
            developmentIdentityEnabled,
            developmentIdentityKey: 'superadmin',
            wecomLoginEnabled,
          }}
          runtime={{
            acceptanceIdentityPort:
              createDisposableAcceptanceIdentityPort(acceptanceStorage),
            maximumIntegrationPermissions,
            refreshStorage: storage,
            subscribeBusinessStorage(listener) {
              // The demo bridge merges local receipts and preserves this tab's edits.
              if (getLocalDemoCatalog()) return () => undefined;
              const handleStorage = (event: StorageEvent) => {
                if (
                  event.storageArea === window.localStorage &&
                  event.key ===
                    (getLocalDemoCatalog()
                      ? 'cpq-local-database-demo:v1:cpq-v2-demo:v5'
                      : 'cpq-v2-demo:v5')
                )
                  listener();
              };
              window.addEventListener('storage', handleStorage);
              return () => window.removeEventListener('storage', handleStorage);
            },
          }}
        />
      </MarketingAnnouncementPrefetchProvider>
    </ActiveSessionProvider>
  );
}

/**
 * 本地开发与临时共享测试只使用一个最大权限投影，避免浏览器中曾保存的演示角色
 * 阻塞真实功能联调。正式企业微信构建不会启用此端口；服务端 RBAC 仍是授权真源。
 */
export function createMaximumIntegrationAcceptanceIdentityPort(
  state: Pick<CpqState, 'catalog'>,
): AcceptanceIdentityPort {
  const role = state.catalog.roles.find(
    (candidate) =>
      candidate.key === 'super_admin' && candidate.status === 'active',
  );
  const user = state.catalog.users.find(
    (candidate) =>
      candidate.status === 'active' &&
      candidate.roleId === role?.id &&
      candidate.role === 'super_admin',
  );
  if (!role || !user) throw new Error('联调最大权限身份不可用');
  const identity = { roleId: role.id, userId: user.id };
  return {
    load: () => identity,
    // 联调模式不允许浏览器选择或持久化其他演示角色。
    save: () => undefined,
    clear: () => undefined,
  };
}

export function createDisposableAcceptanceIdentityPort(
  storage: StorageLike,
): AcceptanceIdentityPort {
  return {
    load() {
      const raw = storage.getItem(DISPOSABLE_ACCEPTANCE_IDENTITY_KEY);
      if (raw === null) return null;
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object')
        throw new Error('标签页验收身份无效');
      const candidate = parsed as { roleId?: unknown; userId?: unknown };
      if (
        typeof candidate.roleId !== 'string' ||
        typeof candidate.userId !== 'string'
      ) {
        throw new Error('标签页验收身份无效');
      }
      return { roleId: candidate.roleId, userId: candidate.userId };
    },
    save(identity) {
      storage.setItem(
        DISPOSABLE_ACCEPTANCE_IDENTITY_KEY,
        JSON.stringify(identity),
      );
    },
    clear() {
      storage.removeItem(DISPOSABLE_ACCEPTANCE_IDENTITY_KEY);
    },
  };
}
